import { clientIdForHost, type RuntimeConfig } from "../config";
import type { FreshTokens } from "./auth";

/**
 * Cognito's user-facing JSON API, called by the browser with the user's own
 * access token (section 13.3). No SDK: each call is one POST to the service
 * root with an X-Amz-Target header and no signature - these operations take
 * the token in the body. Nothing here needs, or has, a server privilege.
 * The auth template's `cognito-api.ts`, verbatim but for the config type.
 */

export class CognitoApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface PasskeyRecord {
  CredentialId: string;
  FriendlyCredentialName?: string;
  AuthenticatorAttachment?: string;
  AuthenticatorTransports?: string[];
  CreatedAt?: number;
  RelyingPartyId?: string;
}

/** WebAuthn is available here: what the passkey card, the step-up dialog and the passkey nudge all check. */
export function passkeysPossible(): boolean {
  return typeof navigator !== "undefined" && "credentials" in navigator && typeof PublicKeyCredential !== "undefined";
}

export function cognitoServiceRoot(config: RuntimeConfig): string {
  return `${new URL(config.cognito?.issuer ?? "https://mock.invalid").origin}/`;
}

export function cognitoApi(config: RuntimeConfig) {
  const root = cognitoServiceRoot(config);
  const clientId = clientIdForHost(config) ?? "";
  const call = async <T>(target: string, body: Record<string, unknown>): Promise<T> => {
    const res = await fetch(root, {
      method: "POST",
      headers: { "content-type": "application/x-amz-json-1.1", "x-amz-target": `AWSCognitoIdentityProviderService.${target}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    const json = (text ? JSON.parse(text) : {}) as Record<string, unknown> & { __type?: string; message?: string };
    if (!res.ok) throw new CognitoApiError(String(json.__type ?? "CognitoError").split("#").pop() ?? "CognitoError", String(json.message ?? `Cognito answered ${res.status}`));
    return json as T;
  };

  return {
    listPasskeys: (accessToken: string) => call<{ Credentials?: PasskeyRecord[] }>("ListWebAuthnCredentials", { AccessToken: accessToken, MaxResults: 20 }),
    startPasskey: (accessToken: string) => call<{ CredentialCreationOptions: unknown }>("StartWebAuthnRegistration", { AccessToken: accessToken }),
    completePasskey: (accessToken: string, credential: unknown) => call<Record<string, never>>("CompleteWebAuthnRegistration", { AccessToken: accessToken, Credential: credential }),
    deletePasskey: (accessToken: string, credentialId: string) => call<Record<string, never>>("DeleteWebAuthnCredential", { AccessToken: accessToken, CredentialId: credentialId }),

    changePassword: (accessToken: string, previous: string, proposed: string) =>
      call<Record<string, never>>("ChangePassword", { AccessToken: accessToken, PreviousPassword: previous, ProposedPassword: proposed }),
    associateSoftwareToken: (accessToken: string) => call<{ SecretCode?: string }>("AssociateSoftwareToken", { AccessToken: accessToken }),
    verifySoftwareToken: (accessToken: string, code: string, deviceName: string) =>
      call<{ Status?: string }>("VerifySoftwareToken", { AccessToken: accessToken, UserCode: code, FriendlyDeviceName: deviceName }),
    setTotpPreference: (accessToken: string) =>
      call<Record<string, never>>("SetUserMFAPreference", { AccessToken: accessToken, SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true } }),
    globalSignOut: (accessToken: string) => call<Record<string, never>>("GlobalSignOut", { AccessToken: accessToken }),

    /** Step-up: a fresh authentication through the choice flow, by passkey or by password + authenticator code. */
    initiateAuth: (username: string, preferred: "WEB_AUTHN" | "PASSWORD", password?: string) =>
      call<{ ChallengeName?: string; Session?: string; ChallengeParameters?: Record<string, string>; AuthenticationResult?: FreshTokens }>("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        ClientId: clientId,
        AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: preferred, ...(password ? { PASSWORD: password } : {}) },
      }),
    respondToChallenge: (challengeName: string, session: string, responses: Record<string, string>) =>
      call<{ ChallengeName?: string; Session?: string; ChallengeParameters?: Record<string, string>; AuthenticationResult?: FreshTokens }>("RespondToAuthChallenge", {
        ChallengeName: challengeName,
        ClientId: clientId,
        Session: session,
        ChallengeResponses: responses,
      }),
  };
}

export type CognitoApi = ReturnType<typeof cognitoApi>;

/** WebAuthn's binary fields travel base64url; the browser API wants ArrayBuffers and gives them back. */
export function toWebAuthnCreationOptions(options: unknown): CredentialCreationOptions {
  const o = options as { publicKey?: Record<string, unknown> } & Record<string, unknown>;
  const pk = (o.publicKey ?? o) as Record<string, unknown>;
  const exclude = (pk["excludeCredentials"] as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromBase64Url(String(pk["challenge"])),
      user: { ...(pk["user"] as { name: string; displayName: string }), id: fromBase64Url(String((pk["user"] as { id: string }).id)) },
      excludeCredentials: exclude.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: fromBase64Url(String(c["id"])) })),
    },
  };
}

export function toWebAuthnRequestOptions(options: unknown): CredentialRequestOptions {
  const o = options as { publicKey?: Record<string, unknown> } & Record<string, unknown>;
  const pk = (o.publicKey ?? o) as Record<string, unknown>;
  const allow = (pk["allowCredentials"] as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromBase64Url(String(pk["challenge"])),
      allowCredentials: allow.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: fromBase64Url(String(c["id"])) })),
    },
  };
}

/** What Cognito wants back: the credential as JSON with binary fields base64url. Modern browsers do this themselves. */
export function credentialToJson(credential: Credential | null): unknown {
  if (!credential) throw new Error("No credential was created");
  const c = credential as PublicKeyCredential & { toJSON?: () => unknown };
  if (typeof c.toJSON === "function") return c.toJSON();
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: toBase64Url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment,
    clientExtensionResults: c.getClientExtensionResults(),
    response: {
      clientDataJSON: toBase64Url(r.clientDataJSON),
      ...(r.attestationObject ? { attestationObject: toBase64Url(r.attestationObject), transports: r.getTransports?.() } : {}),
      ...(r.authenticatorData ? { authenticatorData: toBase64Url(r.authenticatorData), signature: toBase64Url(r.signature), userHandle: r.userHandle ? toBase64Url(r.userHandle) : null } : {}),
    },
  };
}

export function fromBase64Url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function toBase64Url(buf: ArrayBuffer): string {
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
